// v2/components/ProductTable.js
import React, { useState, useMemo, useCallback, useDeferredValue, useEffect, useRef, useLayoutEffect } from 'react';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, XCircle, ArrowRight, PackageSearch } from 'lucide-react';
import { Link } from 'react-router-dom';
import ProductRow, { ProductCard } from './ProductRow';
import QuickView from './QuickView';
import Select from './Select';
import { Skel, skelStyles } from './Skeleton';
import { PAGE_SIZE_OPTIONS } from '../constants/theme';
import { formatNumber } from '../utils/format';
import styles from './ProductTable.module.css';

const COLUMNS = [
  { key: 'product',        label: 'Ürün',            sortable: false, cls: 'colImg' },
  { key: 'productCode',    label: 'Ürün Kodu',       sortable: true,  cls: 'colCode' },
  { key: 'description',    label: 'Ürün Açıklaması', sortable: true,  cls: 'colDesc' },
  { key: 'groupCode',      label: 'Grup',            sortable: true,  cls: 'colGroup', align: 'center' },
  { key: 'discountRate',   label: 'İndirim Zinciri', sortable: true,  cls: 'colChain' },
  { key: 'grossPrice',     label: 'Brüt Fiyat',      sortable: true,  cls: 'colPrice' },
  { key: 'netPrice',       label: 'Net Fiyat',       sortable: true,  cls: 'colPrice' },
  { key: 'vatPrice',       label: "KDV'li Fiyat",    sortable: true,  cls: 'colPrice' },
  { key: 'gaziantepStock', label: 'Gaziantep Depo',  sortable: true,  cls: 'colStock', align: 'center' },
  { key: 'istanbulStock',  label: 'İstanbul Depo',   sortable: true,  cls: 'colStock', align: 'center' },
];

const STOCK_FILTERS = [
  { id: 'all', label: 'Stok: Tümü' },
  { id: 'in',  label: 'Stokta Var' },
  { id: 'gzt', label: 'Gaziantep Stoklu' },
  { id: 'ist', label: 'İstanbul Stoklu' },
  { id: 'out', label: 'Stok Yok' },
];

const CAMPAIGN_FILTERS = [
  { id: 'all', label: 'İndirim: Tümü' },
  { id: 'yes', label: 'İndirimli + Kampanyalı' },
  { id: 'general', label: 'Kampanyalı Ürünler' },
];

const collator = new Intl.Collator('tr', { numeric: true, sensitivity: 'base' });

/** Siralama ikonu — render disinda tanimli (her render'da remount olmasin) */
function SortIcon({ colKey, sort }) {
  if (sort.key !== colKey) return <ChevronsUpDown size={13} className={styles.sortIcon} />;
  const I = sort.dir === 'asc' ? ChevronUp : ChevronDown;
  return <I size={13} className={styles.sortActive} />;
}

/** fixedHeight acikken govdeyi saran kutu; kapaliyken DOM'a hic eklenmez */
function Kap({ on, className, style, children }) {
  return on ? <div className={className} style={style}>{children}</div> : <>{children}</>;
}

/** Mobil mi? — tablo ve kart listesi ayni anda render edilmesin */
const MOBILE_Q = '(max-width: 900px)';
function useIsMobile() {
  const get = () => (typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia(MOBILE_Q).matches);
  const [isMobile, setIsMobile] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(MOBILE_Q);
    const on = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    setIsMobile(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, []);
  return isMobile;
}

export default function ProductTable({
  initialCampaign = 'all',
  initialSearch = '',
  initialStock = 'all',
  products = [], onAddToCart, onOpenDetail,
  showAllLink = false, showOem = true,
  initialPageSize = 25, showPageSizer = true, eurRate = 0,
  showToolbar = true, title = null, hidden = {},
  simpleStock = false, showActions = false, loading = false,
  // true: QuickView hover yerine sepet butonuna tiklayinca acilir (urunler sayfasi)
  // false: eski davranis — satir hover'inda acilir (ana sayfa)
  quickOnClick = false,
  // Sunucu tarafli mod: filtreleme/siralama/sayfalama backend'de yapilir.
  // Verilmezse bilesen eskisi gibi istemci tarafinda calisir.
  serverMode = false,
  serverTotalPages = 1,
  serverTotalItems = 0,
  serverPage = 1,
  onServerChange = null,
  // true: dolu sayfa yuksekligi korunur, az sonucta kart daralmaz (ana sayfa)
  fixedHeight = false,
}) {
  const [term, setTerm] = useState(initialSearch || '');
  const [stock, setStock] = useState(initialStock || 'all');
  const [camp, setCamp] = useState(initialCampaign || 'all');
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [page, setPage] = useState(1);
  const [quick, setQuick] = useState(null);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const isMobile = useIsMobile();


  const columns = useMemo(() => {
    const base = COLUMNS.filter((c) => {
      if (c.key === 'discountRate' && hidden.chain) return false;
      if (c.key === 'vatPrice' && hidden.vat) return false;
      return true;
    }).map((c) => (simpleStock && c.cls === 'colStock'
      ? { ...c, cls: 'colStockSm', label: c.label.replace(' Depo', '') }
      : c));
    return showActions
      ? [...base, { key: 'actions', label: 'İşlem', sortable: false, cls: 'colAction', align: 'center' }]
      : base;
  }, [hidden.chain, hidden.vat, simpleStock, showActions]);

  const deferredTerm = useDeferredValue(term);

  const filtered = useMemo(() => {
    if (serverMode) return products;   // filtreleme backend'de yapildi
    const q = deferredTerm.trim().toLocaleLowerCase('tr');
    return products.filter((p) => {
      if (q) {
        const hay = [p.productCode, p.description, p.brand, p.groupCode, ...(p.oem || [])]
          .join(' ').toLocaleLowerCase('tr');
        if (!hay.includes(q)) return false;
      }
      const tot = p.gaziantepStock + p.istanbulStock;
      if (stock === 'in' && tot <= 0) return false;
      if (stock === 'out' && tot > 0) return false;
      if (stock === 'gzt' && p.gaziantepStock <= 0) return false;
      if (stock === 'ist' && p.istanbulStock <= 0) return false;
      if (camp === 'yes' && !p.campaign) return false;
      if (camp === 'general' && !p.hasGeneralCampaign) return false;
      return true;
    });
  }, [products, deferredTerm, stock, camp, serverMode]);

  const sorted = useMemo(() => {
    if (serverMode || !sort.key) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
      return collator.compare(String(x), String(y)) * dir;
    });
  }, [filtered, sort, serverMode]);

  const totalPages = serverMode
    ? Math.max(serverTotalPages, 1)
    : Math.max(Math.ceil(sorted.length / pageSize), 1);
  const current = serverMode ? serverPage : Math.min(page, totalPages);
  const visible = useMemo(
    () => (serverMode ? sorted : sorted.slice((current - 1) * pageSize, current * pageSize)),
    [sorted, current, pageSize, serverMode]
  );
  const toplamAdet = serverMode ? serverTotalItems : sorted.length;

  // Sabit yukseklik: dolu sayfa cizilince tablonun gercek yuksekligi olculur,
  // az sonucta bu deger min-height olarak kullanilir (kirilimlara gore dogru olcu).
  const tabloRef = useRef(null);
  const [govdeH, setGovdeH] = useState(null);
  const sabit = fixedHeight && !isMobile;
  useLayoutEffect(() => {
    if (!sabit || !tabloRef.current) return;
    const tamSayfa = !loading && visible.length >= pageSize;
    const ilkIskelet = loading && govdeH == null;   // henuz olcu yoksa iskeletten al
    if (!tamSayfa && !ilkIskelet) return;
    const h = tabloRef.current.offsetHeight;
    if (h > 0 && (govdeH == null || Math.abs(h - govdeH) > 1)) setGovdeH(h);
  }, [sabit, loading, visible.length, pageSize, govdeH]);

  // Sunucu modunda filtre/siralama/sayfa degisikligini disariya bildir
  const bildir = useCallback((yama) => {
    if (serverMode && onServerChange) onServerChange(yama);
  }, [serverMode, onServerChange]);

  // filtre/siralama/sayfa degisince kisa fade — tum bagimliliklar yukarida tanimli
  const listKey = `${deferredTerm}|${stock}|${camp}`
    + `|${sort.key}|${sort.dir}|${current}|${pageSize}`;

  const quickTimer = React.useRef(null);

  const openQuick = useCallback((product, anchor) => {
    clearTimeout(quickTimer.current);
    if (isMobile) return;
    if (quickOnClick) return;   // tiklama modunda hover ile acilmaz
    // kisa gecikme: fare tabloyu tararken her satirda panel acilmasin
    quickTimer.current = setTimeout(() => setQuick({ product, anchor }), 220);
  }, [isMobile, quickOnClick]);

  // karta gecerken kapanmasin diye kisa gecikme
  const closeQuick = useCallback(() => {
    clearTimeout(quickTimer.current);
    quickTimer.current = setTimeout(() => setQuick(null), 120);
  }, []);

  const keepQuick = useCallback(() => clearTimeout(quickTimer.current), []);

  /** Tiklama modunda: sepet butonu paneli acar, sepete eklemez */
  const openQuickNow = useCallback((product, anchor) => {
    clearTimeout(quickTimer.current);
    setQuick({ product, anchor });
  }, []);

  useEffect(() => () => clearTimeout(quickTimer.current), []);

  useEffect(() => {
    if (!quick) return undefined;
    const hide = () => setQuick(null);
    const esc = (e) => { if (e.key === 'Escape') hide(); };
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [quick]);

  const SUNUCU_SIRA = {
    productCode: 'code', description: 'name', groupCode: 'stgrpcode',
    grossPrice: 'price', netPrice: 'price',
    gaziantepStock: 'stock', istanbulStock: 'stock', discountRate: null, vatPrice: null,
  };
  const toggleSort = useCallback((key) => {
    setSort((s) => {
      const yeni = s.key === key
        ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' };
      const alan = SUNUCU_SIRA[key];
      if (alan) bildir({ sort: `${alan}-${yeni.dir}`, page: 1 });
      return yeni;
    });
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bildir]);

  const change = (setter, alan) => (e) => {
    setter(e.target.value); setPage(1); bildir({ [alan]: e.target.value, page: 1 });
  };
  // ortak Select dogrudan degeri verir (olay nesnesi degil)
  const degistir = (setter, alan) => (v) => {
    setter(v); setPage(1); bildir({ [alan]: v, page: 1 });
  };
  const activeCount = (term ? 1 : 0) + (stock !== 'all' ? 1 : 0) + (camp !== 'all' ? 1 : 0);
  const dirty = activeCount > 0;
  const clearAll = () => {
    setTerm(''); setStock('all'); setCamp('all'); setPage(1);
    bildir({ search: '', stock: 'all', campaign: 'all', page: 1 });
  };

  const pageList = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '…', totalPages];
    if (current >= totalPages - 3) return [1, '…', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, '…', current - 1, current, current + 1, '…', totalPages];
  }, [current, totalPages]);

  return (
    <section className={styles.panel} aria-label="Ürün listesi">
      {title && (
        <div className={styles.panelHead}>
          <span className={styles.panelBar} />
          <h2 className={styles.panelTitle}>{title}</h2>
          <span className={styles.panelSpacer} />
          {showAllLink && (
            <Link className={styles.panelLink} to="/v2/products">
              Tüm Ürünleri Görüntüle <ArrowRight size={14} />
            </Link>
          )}
        </div>
      )}

      {showToolbar && (
      <div className={styles.toolbar}>
        <div className={styles.tbSearch}>
          <span className={styles.tbSearchIcon}><Search size={15} /></span>
          <input
            type="text" value={term} placeholder="Ürün ara..."
            onChange={(e) => {
              setTerm(e.target.value); setPage(1);
              bildir({ search: e.target.value, page: 1 });
            }}
            aria-label="Tabloda ara"
          />
        </div>
        <Select
          value={stock} on={stock !== 'all'} ariaLabel="Stok"
          className={styles.tbSel}
          options={STOCK_FILTERS.map((f) => ({ value: f.id, label: f.label }))}
          onChange={degistir(setStock, 'stock')}
        />

        <Select
          value={camp} on={camp !== 'all'} ariaLabel="Kampanya"
          className={styles.tbSel}
          options={CAMPAIGN_FILTERS.map((f) => ({ value: f.id, label: f.label }))}
          onChange={degistir(setCamp, 'campaign')}
        />

        <span className={styles.tbSpacer} />
        <span className={styles.tbCount}><b>{formatNumber(toplamAdet)}</b> ürün</span>
        <button type="button" className={styles.tbClear} onClick={clearAll} disabled={!dirty}>
          <XCircle size={14} /> Filtreleri Temizle{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
        {showAllLink && (
          <Link className={styles.allLink} to="/v2/products">
            Tüm Ürünleri Görüntüle <ArrowRight size={14} />
          </Link>
        )}
      </div>
      )}

      <Kap on={sabit} className={styles.fixedBody} style={govdeH ? { minHeight: govdeH } : undefined}>
      {!isMobile && (
      <div ref={tabloRef} className={simpleStock ? styles.compactScroll : styles.scroll}>
        <table className={`${styles.table} ${simpleStock ? styles.tableCompact : ''}`}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={[
                    styles[c.cls] || '',
                    c.align === 'center' ? styles.center : '',
                    c.sortable ? styles.sortable : '',
                    sort.key === c.key ? styles.thSorted : '',
                  ].join(' ')}
                  onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                  onKeyDown={c.sortable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSort(c.key); }
                  } : undefined}
                  tabIndex={c.sortable ? 0 : undefined}
                  role={c.sortable ? 'button' : undefined}
                  aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <span className={styles.thInner}>
                    {c.label}
                    {c.sortable && <SortIcon colKey={c.key} sort={sort} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody key={listKey} className={styles.listFade}>
            {loading && Array.from({ length: 8 }).map((_, i) => (
              <tr key={`skel-${i}`} className={`${styles.row} ${skelStyles[`d${i % 8}`]}`}>
                {columns.map((c) => (
                  <td key={c.key} className={c.align === 'center' ? styles.center : ''}>
                    {c.cls === 'colImg'
                      ? <Skel w={simpleStock ? 32 : 40} h={simpleStock ? 32 : 40} shape="box" />
                      : c.cls === 'colDesc'
                        ? <Skel w={i % 3 === 0 ? '58%' : i % 3 === 1 ? '76%' : '66%'} h={12} />
                        : <Skel w={c.align === 'center' ? '72%' : '84%'} h={11} />}
                  </td>
                ))}
              </tr>
            ))}
            {!loading && visible.map((p) => (
              <ProductRow
                key={p.id} product={p}
                onAddToCart={onAddToCart} onOpenDetail={onOpenDetail}
                showOem={showOem} eurRate={eurRate}
                hidden={hidden} simpleStock={simpleStock} showActions={showActions}
                onQuickEnter={openQuick} onQuickLeave={closeQuick}
                quickOnClick={quickOnClick} onQuickOpen={openQuickNow}
                isActive={!!quick && quick.product.id === p.id}
              />
            ))}
          </tbody>
        </table>
      </div>
      )}

      {isMobile && (
      <div key={listKey} className={`${styles.mobileList} ${styles.listFade}`}>
        {visible.map((p) => (
          <ProductCard
            key={p.id} product={p}
            onAddToCart={onAddToCart} onOpenDetail={onOpenDetail}
          />
        ))}
      </div>
      )}

      {visible.length === 0 && (
        <div className={styles.empty}>
          <span className={styles.emptyIcon}><PackageSearch size={34} strokeWidth={1.3} /></span>
          <span className={styles.emptyTitle}>Ürün bulunamadı</span>
          <span className={styles.emptyText}>
            {dirty ? 'Filtreleri gevşetip tekrar deneyin.' : 'Listelenecek ürün yok.'}
          </span>
          {dirty && (
            <button type="button" className={styles.emptyBtn} onClick={clearAll}>
              <XCircle size={14} /> Filtreleri Temizle
            </button>
          )}
        </div>
      )}
      </Kap>

      {quick && (
        <QuickView
          product={quick.product} anchor={quick.anchor} eurRate={eurRate}
          onEnter={keepQuick}
          onLeave={() => setQuick(null)}
          onAddToCart={(p, q, w) => { onAddToCart(p, q, w); setQuick(null); }}
          onOpenDetail={(p) => { setQuick(null); onOpenDetail(p); }}
        />
      )}

      <div className={styles.pager}>
        {showPageSizer && (
        <span className={styles.pagerLabel}>
          Sayfa başına:
          <Select
            value={pageSize} ariaLabel="Sayfa başına ürün"
            className={styles.tbSelSm}
            options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
            onChange={(v) => {
              setPageSize(Number(v)); setPage(1);
              bildir({ perPage: Number(v), page: 1 });
            }}
          />
        </span>
        )}
        <div className={styles.pages}>
          <button type="button" className={styles.pageBtn}
                  onClick={() => { setPage(current - 1); bildir({ page: current - 1 }); }}
                  disabled={current <= 1}
                  aria-label="Önceki sayfa"><ChevronLeft size={15} /></button>
          {pageList.map((n, i) => (n === '…' ? (
            <span key={`d${i}`} className={styles.dots}>…</span>
          ) : (
            <button
              key={n} type="button"
              className={`${styles.pageBtn} ${n === current ? styles.pageActive : ''}`}
              onClick={() => { setPage(n); bildir({ page: n }); }}
            >{n}</button>
          )))}
          <button type="button" className={styles.pageBtn}
                  onClick={() => { setPage(current + 1); bildir({ page: current + 1 }); }}
                  disabled={current >= totalPages}
                  aria-label="Sonraki sayfa"><ChevronRight size={15} /></button>
        </div>
      </div>
    </section>
  );
}
